import { useQuery } from '@tanstack/react-query';
import { formatDistanceStrict, parseJSON } from 'date-fns';
import { sumBy } from 'lodash';
import React from 'react';
import { useWatch } from 'react-hook-form-mui';
import { useNavigate } from 'react-router';
import { Button, Stack } from '@mui/material';
import {
    GridColDef,
    GridPaginationModel,
    GridRowParams,
} from '@mui/x-data-grid';

import {
    DataGrid,
    DataGridCard,
    dataGridNestedObjectValueGetter,
} from '@barboxx/admin-components';
import {
    currencyFormatter,
    dateFormatter,
    ID_WIDTH,
    useAuth,
    useBusinessSelector,
    usePaging,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Customer, Order } from '@barboxx/models';
import { BusinessPickupMode } from '@barboxx/types';
import { getBusinessDayQueryRange } from '@barboxx/utils';

import {
    downloadCsv,
    fetchAllOrdersForExport,
    getOrdersExportColumns,
} from './exportUtils';

const PICKUP_MODE_LABEL: Record<BusinessPickupMode, string> = {
    Swipe: 'Swipe',
    Nfc: 'NFC',
    NfcWithFace: 'NFC + Face',
};

const COLUMNS: GridColDef<Order.DetailsResponse>[] = [
    { field: 'id', headerName: 'Id', width: ID_WIDTH },
    {
        field: 'business.place.name',
        headerName: 'Business',
        valueGetter: dataGridNestedObjectValueGetter,
        sortable: false,
        width: 200,
    },
    {
        field: 'status',
        headerName: 'Status',
        width: 100,
    },
    {
        field: 'business.pickupMode',
        headerName: 'Pickup Method',
        sortable: false,
        width: 130,
        valueGetter: (value, row) => {
            const mode = row.business?.pickupMode as
                | BusinessPickupMode
                | undefined;
            return mode ? (PICKUP_MODE_LABEL[mode] ?? mode) : '—';
        },
    },
    {
        field: 'subTotal',
        headerName: 'SubTotal',
        width: 100,
        valueFormatter: currencyFormatter,
    },
    {
        field: 'serviceFee',
        headerName: 'Service Fee',
        width: 100,
        valueFormatter: currencyFormatter,
    },
    {
        field: 'taxAmount',
        headerName: 'Tax',
        width: 100,
        valueFormatter: currencyFormatter,
    },
    {
        field: 'tipAmount',
        headerName: 'Tip',
        width: 100,
        valueFormatter: currencyFormatter,
    },
    {
        field: 'totalAmount',
        headerName: 'Total',
        width: 100,
        valueFormatter: currencyFormatter,
    },
    {
        field: 'expectedPayout',
        headerName: 'Expected Payout',
        width: 150,
        sortable: false,
        valueFormatter: currencyFormatter,
    },
    {
        field: 'applicationFee',
        headerName: 'App Fee',
        width: 100,
        valueFormatter: currencyFormatter,
    },
    {
        field: 'creditCardProcessingFee',
        headerName: 'Credit Card Fees',
        width: 150,
        valueFormatter: currencyFormatter,
    },
    {
        field: 'itemCount',
        headerName: 'Item Count',
        width: 100,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (value, row) => sumBy(row.items, i => i.quantity),
    },
    {
        field: 'createdAt',
        headerName: 'Created At',
        width: 200,
        valueFormatter: dateFormatter,
    },
    {
        field: 'totalTime',
        headerName: 'Time to Complete',
        width: 200,
        sortable: false,

        valueGetter: (value, row, column, apiRef) => {
            const startTime = row.createdAt ? parseJSON(row.createdAt) : null;
            const endTime = row?.completedAt
                ? parseJSON(row.completedAt)
                : null;

            if (startTime && endTime) {
                return formatDistanceStrict(endTime, startTime);
            } else {
                return null;
            }
        },
    },
    {
        field: 'customer',
        headerName: 'Customer',
        width: 200,
        valueGetter: (value: Customer, row) =>
            `${value?.firstName} ${value?.lastName}` || 'Unknown',
    },
];

export function OrdersTable() {
    const navigate = useNavigate();
    const {
        auth: { user },
    } = useAuth();
    const { businessId, businessList } = useBusinessSelector();

    const selectedBusiness = React.useMemo(() => {
        if (businessId) {
            return businessList.find(business => business.id === businessId);
        }

        if (user.userType === 'Business' && businessList.length === 1) {
            return businessList[0];
        }

        return undefined;
    }, [businessId, businessList, user.userType]);

    const { status, startedAt, endedAt } = useWatch();
    const { startDateTime, endDateTime } = getBusinessDayQueryRange(
        startedAt,
        endedAt,
        {
            hoursOfOperations: selectedBusiness?.place?.hoursOfOperations,
        }
    );

    const { page, size, sort, updatePageModel, updateSort } = usePaging();
    const [isExporting, setIsExporting] = React.useState(false);
    const { data, isLoading, error } = useQuery({
        queryKey: [
            'orders',
            page,
            size,
            sort,
            status,
            businessId,
            startDateTime,
            endDateTime,
        ],
        queryFn: () =>
            Api.Orders.Order.getOrdersData({
                page,
                size,
                sort,
                status,
                businessId,
                startDateTime,
                endDateTime,
            }),
    });

    const handleChangePageModel = (pageModel: GridPaginationModel) => {
        updatePageModel(pageModel);
    };

    const handleRowClick = (param: GridRowParams) => {
        navigate(`${param.id}`);
    };

    const FINAL_COLUMNS = React.useMemo(() => {
        return COLUMNS.filter(c => {
            if (user.userType === 'Internal') {
                return true;
            } else {
                return ![
                    'serviceFee',
                    'applicationFee',
                    'creditCardProcessingFee',
                    'totalAmount',
                    'customer',
                ].includes(c.field);
            }
        });
    }, [user.userType]);

    const exportColumns = React.useMemo(
        () => getOrdersExportColumns(user.userType),
        [user.userType]
    );

    const handleExportOrders = React.useCallback(async () => {
        try {
            setIsExporting(true);
            const exportRows = await fetchAllOrdersForExport({
                sort,
                status,
                businessId,
                startDateTime,
                endDateTime,
            });

            downloadCsv(
                `sales-orders-${new Date().toISOString()}.csv`,
                exportRows,
                exportColumns
            );
        } finally {
            setIsExporting(false);
        }
    }, [sort, status, businessId, startDateTime, endDateTime, exportColumns]);

    const HeaderComponent = React.useCallback(
        () => (
            <Stack direction="row" spacing={1}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleExportOrders}
                    disabled={isLoading || isExporting}
                >
                    {isExporting ? 'Exporting...' : 'Export Orders CSV'}
                </Button>
            </Stack>
        ),
        [handleExportOrders, isExporting, isLoading]
    );

    return (
        <DataGridCard HeaderComponent={HeaderComponent}>
            <DataGrid
                columns={FINAL_COLUMNS}
                disableColumnMenu
                disableRowSelectionOnClick
                error={error}
                loading={isLoading}
                onRowClick={handleRowClick}
                paginationModel={{
                    page,
                    pageSize: size,
                }}
                onPaginationModelChange={handleChangePageModel}
                onSortModelChange={updateSort}
                rows={data?.data?.results ?? []}
                rowCount={data?.data?.total ?? 0}
            />
        </DataGridCard>
    );
}
